import { createSelector } from '@ngrx/store';
import { selectDigitaixState } from '../core.selectors';
import {
  DEFAULT_PERMISSION_SCOPES_MAP,
  DEFAULT_PERMISSION_TYPES_MAP,
} from './constants';
import { ConfigState } from '@digitaix/types';

// redefined to prevent circular imports
const getUserProfileSettings = createSelector(
  selectDigitaixState,
  state =>
    (state && state.auth && state.auth.user && state.auth.user.settings) || {}
);

// export const selectConfigState = createSelector(
//   selectDigitaixState,
//   getUserProfileSettings,
//   (state, userSettings) => (state && state.config && all([state.config, userSettings])) as ConfigState || null
// );

export const selectConfigState = createSelector(
  selectDigitaixState,
  state => ((state && state.config) as any) || null
);

export const getFirebaseConfig = createSelector(
  selectConfigState,
  config => (config && config.firebase) || null
);

export const hasFirebaseConfig = createSelector(
  getFirebaseConfig,
  firebase => !!firebase
);

export const getFirebaseProjectId = createSelector(
  getFirebaseConfig,
  firebase =>
    (firebase && firebase.options && firebase.options.projectId) || null
);

export const getFirebaseMessagingSenderId = createSelector(
  getFirebaseConfig,
  firebase =>
    (firebase && firebase.options && firebase.options.messagingSenderId) || null
);

export const getFeatureFlagsConfig = createSelector(
  selectConfigState,
  config => (config && config.featureFlags) || null
);

export const hasFeatureFlagsConfig = createSelector(
  getFeatureFlagsConfig,
  featureFlags => !!featureFlags
);

// region .environment

export const getEnvironmentConfig = createSelector(
  selectConfigState,
  state => (state && state.environment) || null
);

export const getEnvironmentName = createSelector(
  getEnvironmentConfig,
  environment => (environment && environment.name) || null
);

export const getEnvironmentSlug = createSelector(
  getEnvironmentConfig,
  environment => (environment && environment.slug) || null
);

export const isProductionMode = createSelector(
  getEnvironmentConfig,
  environment => {
    if (environment) {
      if (environment.production !== undefined) {
        return environment.production;
      }
      return environment.slug === 'production';
    }
    return false;
  }
);

/**
 * @deprecated use isProductionMode
 */
export const isProduction = isProductionMode;

export const isDevelopmentMode = createSelector(
  isProductionMode,
  p => !p
);

export const getEnvironmentUrl = createSelector(
  getEnvironmentConfig,
  environment => (environment && environment.url) || null
);

// endregion

export const getGitlabConfig = createSelector(
  selectConfigState,
  state => (state && state.gitlab) || null
);

export const getAuthConfig = createSelector(
  selectConfigState,
  state => (state && state.authentication) || null
);

export const getGitlabUrl = createSelector(
  getGitlabConfig,
  gitlab => gitlab && gitlab.url
);

export const getGitlabIssueUrl = createSelector(
  getGitlabUrl,
  url => url && url + '/issues/new'
);

export const getFirebaseAuthConfig = createSelector(
  getFirebaseConfig,
  config => (config && config.auth) || null
);

// region .authentication

export const requireVerifiedEmail = createSelector(
  getAuthConfig,
  config => (config && config.verifyEmail) || false
);

export const usernameLoginEnabled = createSelector(
  getAuthConfig,
  config => (config && config.usernameLogin) || false
);

export const signUpEnabled = createSelector(
  getAuthConfig,
  config => (config && config.signUp) || false
);

export const resetPasswordEnabled = createSelector(
  getAuthConfig,
  config => (config && config.resetPassword) || false
);

export const userWithoutRoles = createSelector(
  getAuthConfig,
  config => (config && config.withoutRoles) || false,
);

export const isAuthEnabled = createSelector(
  getAuthConfig,
  config => {
    if (config && config.hasOwnProperty('enabled')) {
      return config.enabled;
    }
    return null;
  }
);

// region .authentication.oauth

export const getOAuthConfig = createSelector(
  getAuthConfig,
  config => (config && config.oauth) || null
);

export const getOAuthEndpoint = createSelector(
  getOAuthConfig,
  config => (config && config.authEndpoint) || null
);

export const getOAuthProfileEndpoint = createSelector(
  getOAuthConfig,
  config => (config && config.profileEndpoint) || null
);

export const getOAuthGrantType = createSelector(
  getOAuthConfig,
  config => (config && config.grantType) || null
);

export const getOAuthSecret = createSelector(
  getOAuthConfig,
  config => (config && config.secret) || null
);

// endregion

// endregion

// region .authorisation

export const getAuthorisationConfig = createSelector(
  selectConfigState,
  state => (state && state.authorisation) || null
);

export const getAuthorisationRoles = createSelector(
  getAuthorisationConfig,
  config => (config && config.roles) || []
);

export const getAuthorisationScopes = createSelector(
  getAuthorisationConfig,
  config => (config && config.scopes) || DEFAULT_PERMISSION_SCOPES_MAP
);

export const getAuthorisationPermissionTypes = createSelector(
  getAuthorisationConfig,
  config => (config && config.types) || DEFAULT_PERMISSION_TYPES_MAP
);

// endregion

// region .api

export const getApiConfig = createSelector(
  selectConfigState,
  state => (state && state.api) || null
);

export const getApiBaseUrl = createSelector(
  getApiConfig,
  config => (config && config.baseUrl) || null
);

// endregion

// region .appName

export const getAppName = createSelector(
  selectConfigState,
  state => state && state.appName
);

// endregion

// region .stackdriver

export const getStackdriverConfig = createSelector(
  selectConfigState,
  (state: ConfigState) => (state && state.stackdriver) || { key: null, projectId: null }
);

export const getStackdriverKey = createSelector(
  getStackdriverConfig,
  config => config.key || null
);

export const getStackdriverProjectId = createSelector(
  getStackdriverConfig,
  config => config.projectId || null
);

// endregion

export const googleLogin = createSelector(
  getFirebaseAuthConfig,
  config => (config && config.google) || false
);

export const facebookLogin = createSelector(
  getFirebaseAuthConfig,
  config => (config && config.facebook) || false
);

export const twitterLogin = createSelector(
  getFirebaseAuthConfig,
  config => (config && config.twitter) || false
);

export const githubLogin = createSelector(
  getFirebaseAuthConfig,
  config => (config && config.github) || false
);

// region .

export const getAppVersion = createSelector(
  selectConfigState,
  state => state && state.version
);

export const getAppSha = createSelector(
  selectConfigState,
  state => state && state.sha
);

// endregion

// region .components

export const getComponentsConfig = createSelector(
  selectConfigState,
  state => state && state.components
);

// region .components.dx

export const getDxComponentConfig = createSelector(
  getComponentsConfig,
  config => config && config.dx
);

// region .components.dx.auth

export const getDxAuthComponentConfig = createSelector(
  getDxComponentConfig,
  config => config && config.auth
);

export const getDxAuthLogoSrc = createSelector(
  getDxAuthComponentConfig,
  config =>
    (config && config.logoSrc) ||
    'https://digitaix.de/img/logo_digitaix_full.png'
);

export const getDxAuthLogoWidth = createSelector(
  getDxAuthComponentConfig,
  config => (config && config.logoWidth) || '340px'
);

export const getDxAuthLogoHeight = createSelector(
  getDxAuthComponentConfig,
  config => (config && config.logoHeight) || '140px'
);

// endregion

// endregion

// endregion
